import { useRef, useState } from "react";
import { ArrowLeft, ArrowRight, ImagePlus, Star, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { api, errorMessage, mediaUrl } from "@/lib/api";

export const ImageUploader = ({ images = [], coverIndex = 0, onChange, folder = "vehicles" }) => {
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);
  const upload = async (files) => {
    const list = Array.from(files).filter((f) => f.type.startsWith("image/"));
    if (!list.length) return;
    setBusy(true);
    try {
      const fd = new FormData();
      list.forEach((f) => fd.append("files", f));
      const { data } = await api.post(`/admin/media/upload?folder=${folder}`, fd);
      onChange([...images, ...data.map((m) => ({ url: m.url, thumb: m.thumb, alt: "" }))], coverIndex);
      toast.success(`${data.length} image(s) uploaded & optimized`);
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };
  const move = (i, d) => {
    const j = i + d;
    if (j < 0 || j >= images.length) return;
    const arr = [...images];
    [arr[i], arr[j]] = [arr[j], arr[i]];
    let c = coverIndex;
    if (c === i) c = j; else if (c === j) c = i;
    onChange(arr, c);
  };
  const remove = (i) => {
    const arr = images.filter((_, k) => k !== i);
    onChange(arr, Math.max(0, coverIndex >= i ? coverIndex - 1 : coverIndex));
  };
  const replace = (i) => {
    const inp = document.createElement("input");
    inp.type = "file"; inp.accept = "image/*";
    inp.onchange = async () => {
      const fd = new FormData();
      fd.append("files", inp.files[0]);
      try {
        const { data } = await api.post(`/admin/media/upload?folder=${folder}`, fd);
        const arr = [...images];
        arr[i] = { ...arr[i], url: data[0].url, thumb: data[0].thumb };
        onChange(arr, coverIndex);
      } catch (e) { toast.error(errorMessage(e)); }
    };
    inp.click();
  };
  return (
    <div data-testid="image-uploader">
      <div onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); upload(e.dataTransfer.files); }} onClick={() => ref.current?.click()}
        className="flex cursor-pointer flex-col items-center gap-2 border border-dashed border-white/20 p-6 text-center hover:border-white/40" data-testid="image-dropzone">
        <ImagePlus className="text-[#8b95a5]" size={20} />
        <p className="text-xs text-[#c7ccd4]">{busy ? "Uploading & optimizing…" : "Drag & drop images or click — converted to WebP automatically"}</p>
        <input ref={ref} type="file" hidden multiple accept="image/*" onChange={(e) => upload(e.target.files)} data-testid="image-input" />
      </div>
      {images.length > 0 && (
        <div className="mt-4 grid grid-cols-3 gap-3 sm:grid-cols-4 lg:grid-cols-6">
          {images.map((img, i) => (
            <div key={img.url + i} className={`group relative aspect-[4/3] overflow-hidden border ${i === coverIndex ? "border-[#e2c89d]" : "border-white/10"}`} data-testid={`image-item-${i}`}>
              <img src={mediaUrl(img.thumb || img.url)} alt="" className="h-full w-full object-cover" />
              {i === coverIndex && <span className="mono absolute left-1.5 top-1.5 bg-[#e2c89d] px-1.5 py-0.5 text-[0.5rem] text-black">COVER</span>}
              <div className="absolute inset-x-0 bottom-0 flex justify-between bg-black/70 p-1 opacity-0 transition-opacity group-hover:opacity-100">
                <button type="button" onClick={() => move(i, -1)} className="p-1"><ArrowLeft size={12} /></button>
                <button type="button" onClick={() => onChange(images, i)} className="p-1" title="Set as cover" data-testid={`image-cover-${i}`}><Star size={12} /></button>
                <button type="button" onClick={() => replace(i)} className="p-1" title="Replace"><X size={12} className="rotate-45" /></button>
                <button type="button" onClick={() => remove(i)} className="p-1 text-red-300" data-testid={`image-delete-${i}`}><Trash2 size={12} /></button>
                <button type="button" onClick={() => move(i, 1)} className="p-1"><ArrowRight size={12} /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
